import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, FileText } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaperViewPage = () => {
  const { id } = useParams();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaper = async () => {
      try {
        const response = await api.get(`/api/papers/${id}`);
        setPaper(response.data);
      } catch (error) {
        console.error('Error fetching paper:', error);
      }
      setLoading(false);
    };
    fetchPaper();
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <section className="py-32 text-center">
          <p className="text-[var(--sh-grey)]">Loading...</p>
        </section>
      </Layout>
    );
  }

  if (!paper) {
    return (
      <Layout>
        <section className="py-32 text-center">
          <h1 className="font-serif text-3xl mb-4">Article not found</h1>
          <Link to="/insights" className="btn-secondary">Back to Insights</Link>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={`${paper.title} | Sandend House`}
        description={paper.abstract}
        path={`/insights/${paper.id}`}
        article={{ author: paper.author, publishedTime: paper.publishDate }}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ScholarlyArticle",
          "headline": paper.title,
          "abstract": paper.abstract,
          "author": { "@type": "Person", "name": paper.author },
          "datePublished": paper.publishDate,
          "publisher": { "@id": "https://theknox.co.uk/#organization" },
          "url": `https://theknox.co.uk/insights/${paper.id}`
        }}
      />

      <section className="py-20 lg:py-28">
        <div className="section-container">
          <Link to="/insights" className="inline-flex items-center gap-2 text-[var(--sh-grey)] hover:text-[var(--sh-ink)] transition-colors text-sm mb-8" data-testid="back-link">
            <ArrowLeft size={16} /> Back to Insights
          </Link>

          <div className="reading-width">
            <p className="eyebrow mb-3">{paper.status || 'Publication'}</p>
            <h1 className="font-serif text-3xl lg:text-4xl mb-4" data-testid="paper-title">{paper.title}</h1>
            <p className="text-[var(--sh-grey)] text-sm mb-2">
              {paper.author} &middot; {paper.publishDate || paper.date}
            </p>
            {paper.tags && paper.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {paper.tags.map((tag, i) => (
                  <span key={i} className="text-xs text-[var(--sh-grey)] border border-[var(--sh-rule)] px-2 py-0.5 rounded-sm">{tag}</span>
                ))}
              </div>
            )}

            {paper.abstract && (
              <div className="border-l-2 border-[var(--sh-brass)] pl-6 mb-8">
                <p className="text-[var(--sh-charcoal)] italic leading-relaxed">{paper.abstract}</p>
              </div>
            )}

            {paper.filename && (
              <div className="border border-[var(--sh-rule)] rounded p-6 bg-white">
                <div className="flex items-center gap-4 mb-4">
                  <FileText size={24} className="text-[var(--sh-grey)]" />
                  <div>
                    <p className="text-[var(--sh-charcoal)] font-medium text-sm">Full Document</p>
                    <p className="text-[var(--sh-grey)] text-xs">{paper.filename}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={`${API_URL}/api/uploads/${paper.filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-sm !py-2 !px-4"
                    data-testid="view-pdf"
                  >
                    View PDF
                  </a>
                  <a
                    href={`${API_URL}/api/downloads/${paper.filename}`}
                    className="btn-primary text-sm !py-2 !px-4"
                    data-testid="download-pdf"
                  >
                    <Download size={14} className="mr-2" /> Download
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PaperViewPage;
